import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ScheduleService } from "@/features/Assistant/services/scheduleService";
import { ConfigService } from "@/features/Assistant/services/configService";
import type {
  ScheduleData,
  UpcomingMeeting,
  ActiveCall,
} from "@/features/Assistant/types/schedule";
import type { SSESubscription } from "@/shared/utils/fetchSSE";

/**
 * União discriminada por `type`: o TypeScript estreita `data` sozinho, então o modal lê
 * cada variante sem cast — e um novo tipo de detalhe não compila até ser tratado lá.
 */
export type DetailModalData =
  | { type: "meeting"; data: UpcomingMeeting }
  | { type: "call"; data: ActiveCall };

/**
 * The sortable columns are not all strings — `maxWaitMinutes` is a number — so the
 * comparison is chosen by the value's own type instead of coercing everything to text.
 */
const compareValues = (left: unknown, right: unknown): number => {
  if (typeof left === "number" || typeof right === "number") {
    return (typeof left === "number" ? left : 0) - (typeof right === "number" ? right : 0);
  }
  return String(left ?? "").localeCompare(String(right ?? ""));
};

/**
 * Recorte de "Próximas Reuniões". A janela lida já é curta e configurável, então o
 * filtro é feito sobre o que está na tela — nada volta ao servidor.
 */
export type MeetingPeriod = "today" | "tomorrow" | "all";

/**
 * Situação da reunião como filtro: `ready` é a que será atendida, `rejected` a que
 * o Prodgy reconheceu mas não vai atender. É a mesma distinção que a coluna Situação
 * mostra — o filtro apenas recorta por ela.
 */
export type MeetingStatusFilter = "all" | "ready" | "rejected";

const MEETING_STATUS_FILTERS: MeetingStatusFilter[] = ["all", "ready", "rejected"];

/**
 * O seletor entrega `string`, e afirmar o tipo com `as` deixaria qualquer valor entrar
 * no estado — inclusive um que nenhum filtro reconhece, o que esvaziaria a lista sem
 * explicação. Valor não reconhecido cai em "all", que mostra tudo.
 */
const asStatusFilter = (value: string): MeetingStatusFilter =>
  MEETING_STATUS_FILTERS.find((option) => option === value) ?? "all";

const isOnLocalDay = (iso: string | null, offsetDays: number): boolean => {
  if (!iso) return false;
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return false;
  const day = new Date();
  day.setDate(day.getDate() + offsetDays);
  return (
    when.getFullYear() === day.getFullYear() &&
    when.getMonth() === day.getMonth() &&
    when.getDate() === day.getDate()
  );
};

const EMPTY: ScheduleData = {
  summary: { activeCalls: 0, upcomingMeetings: 0, withError: 0 },
  activeCalls: [],
  upcomingMeetings: [],
};

/**
 * Nenhuma resposta chega à tela sem passar por aqui. Um payload de formato diferente
 * — uma api antiga, uma resposta de erro — derrubava o painel inteiro na primeira
 * chamada a `.filter`; agora vira lista vazia, e o operador vê a tela em vez de um
 * erro de aplicação (AB#84794).
 */
const asList = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const normalize = (data: unknown): ScheduleData => {
  const raw = (data ?? {}) as Partial<ScheduleData>;
  const activeCalls = asList<ActiveCall>(raw.activeCalls);
  const upcomingMeetings = asList<UpcomingMeeting>(raw.upcomingMeetings);

  return {
    activeCalls,
    upcomingMeetings,
    summary: {
      activeCalls: raw.summary?.activeCalls ?? activeCalls.length,
      upcomingMeetings: raw.summary?.upcomingMeetings ?? upcomingMeetings.length,
      withError: raw.summary?.withError ?? upcomingMeetings.filter((m) => m.reason).length,
    },
  };
};

export const useScheduleData = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [detailModal, setDetailModal] = useState<DetailModalData | null>(null);
  const [blockMeetingId, setBlockMeetingId] = useState<string | null>(null);
  const [blockSeriesId, setBlockSeriesId] = useState<string | null>(null);
  const [forceLeaveCallId, setForceLeaveCallId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scheduleData, setScheduleData] = useState<ScheduleData>(EMPTY);

  const [meetingPage, setMeetingPage] = useState(1);
  const [callPage, setCallPage] = useState(1);
  const [meetingRowsPerPage, setMeetingRowsPerPage] = useState("10");
  const [callRowsPerPage, setCallRowsPerPage] = useState("10");
  // Abre no dia corrente: a pergunta do operador quase sempre é "o que é hoje".
  const [meetingPeriod, setMeetingPeriodState] = useState<MeetingPeriod>("today");
  const [statusFilter, setStatusFilterState] = useState<MeetingStatusFilter>("all");
  const [workspaceFilter, setWorkspaceFilterState] = useState<string>("all");
  const [meetingSortField, setMeetingSortField] = useState<string>("meetingTime");
  const [meetingSortAsc, setMeetingSortAsc] = useState(true);

  /** Caixa cujo calendário está sendo espelhado — mostrada ao lado da lista. */
  const [mailbox, setMailbox] = useState<string | null>(null);

  const eventSourceRef = useRef<SSESubscription | null>(null);

  useEffect(() => {
    // Falha aqui não derruba o painel: o endereço é contexto, não conteúdo.
    ConfigService.getConfig()
      .then((config) => setMailbox(config.Calendar?.Mailbox ?? null))
      .catch(() => setMailbox(null));
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setScheduleData(normalize(await ScheduleService.getSchedule()));
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load schedule");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();

    const eventSource = ScheduleService.createScheduleStream(
      (data) => {
        setScheduleData(normalize(data));
        setError(null);
      },
      () => setError("Connection to schedule stream lost")
    );
    eventSourceRef.current = eventSource;

    return () => eventSourceRef.current?.close();
  }, []);

  const confirmBlockMeeting = useCallback(async () => {
    if (!blockMeetingId) return;
    try {
      await ScheduleService.cancelMeeting(blockMeetingId);
      setBlockMeetingId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to block meeting");
    }
  }, [blockMeetingId]);

  const confirmBlockSeries = useCallback(async () => {
    if (!blockSeriesId) return;
    try {
      await ScheduleService.blockSeries(blockSeriesId);
      setBlockSeriesId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to block series");
    }
  }, [blockSeriesId]);

  const confirmForceLeaveCall = useCallback(async () => {
    if (!forceLeaveCallId) return;
    try {
      await ScheduleService.forceLeaveCall(forceLeaveCallId);
      setForceLeaveCallId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to force leave call");
    }
  }, [forceLeaveCallId]);

  // Todo filtro volta para a primeira página: manter a página atual mostraria uma
  // tela vazia quando o novo recorte tem menos resultados do que a página em que se está.
  const setMeetingPeriod = (period: MeetingPeriod) => {
    setMeetingPeriodState(period);
    setMeetingPage(1);
  };

  const setStatusFilter = (status: string) => {
    setStatusFilterState(asStatusFilter(status));
    setMeetingPage(1);
  };

  const setWorkspaceFilter = (workspace: string) => {
    setWorkspaceFilterState(workspace);
    setMeetingPage(1);
  };

  const toggleMeetingSort = (field: string) => {
    if (meetingSortField === field) setMeetingSortAsc(!meetingSortAsc);
    else {
      setMeetingSortField(field);
      setMeetingSortAsc(true);
    }
    setMeetingPage(1);
  };

  const getSortIcon = (field: string, currentField: string, isAsc: boolean) =>
    field !== currentField ? null : isAsc ? "↑" : "↓";

  const matches = (...values: (string | null | undefined)[]) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    return values.some((value) => value?.toLowerCase().includes(term));
  };

  /**
   * A busca precisa casar com o que está na tela, não com o dado bruto: quem procura por
   * "14/08 15:00" não encontraria nada comparando contra o ISO em UTC.
   */
  const displayed = (iso: string | null, end?: string | null) =>
    iso ? ScheduleService.formatTimeRange(iso, end ?? undefined) : null;

  const filteredCalls = scheduleData.activeCalls.filter((c) =>
    matches(c.meetingName, c.callId, c.workspace, displayed(c.startTime))
  );
  const inPeriod = (iso: string | null) => {
    if (meetingPeriod === "all") return true;
    return isOnLocalDay(iso, meetingPeriod === "today" ? 0 : 1);
  };

  /** `reason` preenchido é exatamente o que a coluna Situação mostra como rejeitada. */
  const inStatus = (meeting: UpcomingMeeting) =>
    statusFilter === "all" || (statusFilter === "rejected" ? !!meeting.reason : !meeting.reason);

  const inWorkspace = (meeting: UpcomingMeeting) =>
    workspaceFilter === "all" || meeting.workspace === workspaceFilter;

  const filteredMeetings = scheduleData.upcomingMeetings.filter(
    (m) =>
      inPeriod(m.meetingTime) &&
      inStatus(m) &&
      inWorkspace(m) &&
      matches(
        m.meetingName,
        m.fromEmail,
        m.workspace,
        displayed(m.meetingTime, m.meetingEndTime)
      )
  );

  /**
   * Só os workspaces que de fato aparecem na janela lida, e sobre a lista inteira —
   * não sobre a já filtrada, senão escolher um workspace apagaria todos os outros
   * do próprio seletor e não haveria como voltar.
   */
  const workspaceOptions = useMemo(
    () =>
      [...new Set(
        scheduleData.upcomingMeetings
          .map((m) => m.workspace)
          .filter((w): w is string => !!w)
      )].sort((a, b) => a.localeCompare(b)),
    [scheduleData.upcomingMeetings]
  );

  const sortedMeetings = useMemo(
    () =>
      [...filteredMeetings].sort((a, b) => {
        const left = a[meetingSortField as keyof UpcomingMeeting];
        const right = b[meetingSortField as keyof UpcomingMeeting];
        const order = compareValues(left, right);
        return meetingSortAsc ? order : -order;
      }),
    [filteredMeetings, meetingSortField, meetingSortAsc]
  );

  /**
   * How many occurrences of each series are in the window. This is what makes the
   * series information true by construction: it comes from the occurrences the
   * calendar actually has, not from a recurrence rule stored on our side.
   */
  const seriesOccurrences = useMemo(() => {
    const counts = new Map<string, number>();
    for (const meeting of scheduleData.upcomingMeetings) {
      if (!meeting.seriesId) continue;
      counts.set(meeting.seriesId, (counts.get(meeting.seriesId) ?? 0) + 1);
    }
    return counts;
  }, [scheduleData.upcomingMeetings]);

  const paginate = <T,>(items: T[], page: number, rowsPerPage: string) => {
    const size = Number(rowsPerPage);
    return items.slice((page - 1) * size, page * size);
  };

  const totalPages = (count: number, rowsPerPage: string) =>
    Math.max(1, Math.ceil(count / Number(rowsPerPage)));

  return {
    scheduleData,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    detailModal,
    setDetailModal,

    blockMeetingId,
    setBlockMeetingId,
    confirmBlockMeeting,
    blockSeriesId,
    setBlockSeriesId,
    confirmBlockSeries,
    forceLeaveCallId,
    setForceLeaveCallId,
    confirmForceLeaveCall,

    meetings: paginate(sortedMeetings, meetingPage, meetingRowsPerPage),
    meetingPage,
    setMeetingPage,
    meetingRowsPerPage,
    setMeetingRowsPerPage,
    meetingTotalPages: totalPages(sortedMeetings.length, meetingRowsPerPage),
    meetingSortField,
    meetingSortAsc,
    toggleMeetingSort,
    meetingPeriod,
    setMeetingPeriod,
    statusFilter,
    setStatusFilter,
    workspaceFilter,
    setWorkspaceFilter,
    workspaceOptions,


    calls: paginate(filteredCalls, callPage, callRowsPerPage),
    callPage,
    setCallPage,
    callRowsPerPage,
    setCallRowsPerPage,
    callTotalPages: totalPages(filteredCalls.length, callRowsPerPage),

    seriesOccurrences,
    mailbox,
    getSortIcon,
  };
};
